import { makeEntityScaffold } from '@classpass/saga-tools';
import { parseISO } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isNull from 'lodash/isNull';
import _omit from 'lodash/omit';
import _omitBy from 'lodash/omitBy';
import { transformClientResponse, transformKeysToSnakeCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { PREMIUM_CAP_ENTITY, PREMIUM_CAP_RULE } from 'constants/logic';
import URLS from 'constants/urls';

const RULE_ORDER = [
  PREMIUM_CAP_RULE.VENUE_DEFAULT,
  PREMIUM_CAP_RULE.VENUE,
  PREMIUM_CAP_RULE.CLASS,
  PREMIUM_CAP_RULE.RECURRING_SCHEDULE,
  PREMIUM_CAP_RULE.SCHEDULE,
];
const DEFAULT_TIME_START = '00:00:00';
const DEFAULT_TIME_END = '23:45:00';

const getRuleVars = ruleData => {
  const {
    ruleEntity,
    recurrence: { dateStart, dateEnd, timeOfDayStart, timeOfDayEnd, daysOfWeek },
    displayData: { isVenueDefaultRule, isClassRule, isOngoing },
  } = ruleData;
  const isEntityLevel =
    (ruleEntity === PREMIUM_CAP_ENTITY.VENUE && isVenueDefaultRule) ||
    (ruleEntity === PREMIUM_CAP_ENTITY.CLASS && isClassRule);
  // only applies to !isEntityLevel rules
  const includeDateStart = !!dateStart;
  const includeDateEnd = !isOngoing && !!dateEnd;
  const includeTimeOfDay = timeOfDayStart !== DEFAULT_TIME_START || timeOfDayEnd !== DEFAULT_TIME_END;
  const includeDaysOfWeek = daysOfWeek.length !== 0;

  return {
    isEntityLevel,
    recurrence: isEntityLevel
      ? {}
      : {
          recurrence: {
            ...(includeDateStart ? { dateStart } : {}),
            ...(includeDateEnd ? { dateEnd } : {}),
            ...(includeTimeOfDay ? { timeOfDayStart } : {}),
            ...(includeTimeOfDay ? { timeOfDayEnd } : {}),
            ...(includeDaysOfWeek ? { daysOfWeek } : {}),
          },
        },
  };
};

const handleError = error => {
  const data = transformClientResponse(error);
  if (data) {
    return Promise.reject(data);
  }
  reportError(error);
  return Promise.reject(error);
};

const makeVenueData = (venueId, ruleData) => {
  const { spotCap } = ruleData;
  const { isEntityLevel, recurrence } = getRuleVars(ruleData);
  return {
    venueId,
    ruleType: isEntityLevel ? PREMIUM_CAP_RULE.VENUE_DEFAULT : PREMIUM_CAP_RULE.VENUE,
    ruleEntity: PREMIUM_CAP_ENTITY.VENUE,
    ruleEntityId: venueId,
    spotCap,
    ...recurrence,
  };
};

const makeClassData = (venueId, ruleData) => {
  const { ruleEntityId, spotCap } = ruleData;
  const { isEntityLevel, recurrence } = getRuleVars(ruleData);
  return {
    venueId,
    ruleType: isEntityLevel ? PREMIUM_CAP_RULE.CLASS : PREMIUM_CAP_RULE.RECURRING_SCHEDULE,
    ruleEntity: PREMIUM_CAP_ENTITY.CLASS,
    ruleEntityId,
    spotCap,
    ...recurrence,
  };
};

const makeScheduleData = (venueId, ruleData) => {
  const { ruleEntityId, spotCap } = ruleData;
  return {
    venueId,
    ruleType: PREMIUM_CAP_RULE.SCHEDULE,
    ruleEntity: PREMIUM_CAP_ENTITY.SCHEDULE,
    ruleEntityId,
    spotCap,
  };
};

const decorateResponse = rules => {
  return rules
    .sort((a, b) => RULE_ORDER.indexOf(a.ruleType) - RULE_ORDER.indexOf(b.ruleType))
    .map(rule => {
      const { ruleType, recurrence: recurrenceWithNulls = {}, entityMetadata = {} } = rule;
      const recurrence = _omitBy(recurrenceWithNulls, _isNull);
      const { dateStart, dateEnd, timeOfDayStart = DEFAULT_TIME_START, timeOfDayEnd = DEFAULT_TIME_END } = recurrence;
      const { startDate, startTime, endTime } = entityMetadata;

      switch (ruleType) {
        case PREMIUM_CAP_RULE.VENUE:
        case PREMIUM_CAP_RULE.RECURRING_SCHEDULE:
          return {
            ...rule,
            recurrence,
            displayData: {
              startDateTime: dateStart && parseISO(`${dateStart} ${timeOfDayStart}`),
              endDateTime: dateEnd && parseISO(`${dateEnd} ${timeOfDayEnd}`),
              isVenueDefaultRule: false,
              isClassRule: false,
              isOngoing: !dateEnd,
            },
          };

        case PREMIUM_CAP_RULE.SCHEDULE:
          return {
            ...rule,
            displayData: {
              startDateTime: parseISO(`${startDate} ${startTime}`),
              endDateTime: parseISO(`${startDate} ${endTime}`),
            },
          };

        case PREMIUM_CAP_RULE.VENUE_DEFAULT:
        case PREMIUM_CAP_RULE.CLASS:
        default:
          return {
            ...rule,
            displayData: {
              isVenueDefaultRule: true,
              isClassRule: true,
              isOngoing: true,
            },
          };
      }
    });
};

const get = (httpClient, venueId) => {
  const endpoint = URLS.venue.premiumCaps(venueId);

  return httpClient.get(endpoint).catch(handleError).then(transformClientResponse).then(decorateResponse);
};

const post = (httpClient, venueId, ruleData) => {
  const { ruleEntity } = ruleData;
  const endpoint = URLS.venue.premiumCaps(venueId);

  let data = {};
  switch (ruleEntity) {
    case PREMIUM_CAP_ENTITY.VENUE:
      data = makeVenueData(venueId, ruleData);
      break;

    case PREMIUM_CAP_ENTITY.CLASS:
      data = makeClassData(venueId, ruleData);
      break;

    case PREMIUM_CAP_ENTITY.SCHEDULE:
    default:
      data = makeScheduleData(venueId, ruleData);
      break;
  }

  return httpClient
    .post(endpoint, transformKeysToSnakeCase(data))
    .catch(handleError)
    .then(transformClientResponse)
    .then(decorateResponse);
};

const put = (httpClient, venueId, ruleData) => {
  const { id, ruleType } = ruleData;
  const endpoint = URLS.venue.premiumCaps(venueId, id);
  const { recurrence } = getRuleVars(ruleData);

  let data = {};
  switch (ruleType) {
    case PREMIUM_CAP_RULE.VENUE:
    case PREMIUM_CAP_RULE.RECURRING_SCHEDULE:
      data = {
        ...ruleData,
        ...recurrence,
      };
      break;

    case PREMIUM_CAP_RULE.VENUE_DEFAULT:
    case PREMIUM_CAP_RULE.CLASS:
    case PREMIUM_CAP_RULE.SCHEDULE:
    default:
      data = _omit(ruleData, ['recurrence', 'entityMetadata']);
      break;
  }

  return httpClient
    .put(endpoint, transformKeysToSnakeCase(data))
    .catch(handleError)
    .then(transformClientResponse)
    .then(decorateResponse);
};

const remove = (httpClient, venueId, ruleData) => {
  const { id } = ruleData;
  const endpoint = URLS.venue.premiumCaps(venueId, id);
  return httpClient.delete(endpoint).catch(handleError).then(transformClientResponse).then(decorateResponse);
};

const handler = ({ httpClient }, venueId, ruleData = {}) => {
  const { id } = ruleData;

  if (_isEmpty(ruleData)) {
    return get(httpClient, venueId);
  }

  // this is weak but, because rules have a number of required fields
  // we use `ruleData = { id }` as a signal that we are DELETEing
  if (_isEqual(Object.keys(ruleData), ['id'])) {
    return remove(httpClient, venueId, ruleData);
  }

  // `id`s come from the server, signaling that the rule exists
  if (id) {
    return put(httpClient, venueId, ruleData);
  }

  return post(httpClient, venueId, ruleData);
};

export default makeEntityScaffold('SAGA.API.VENUE.PREMIUM_CAPS', handler, { defaultKey: 1 });
